import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isBanned, setIsBanned] = useState<boolean | null>(null);

  // Check ban status
  useEffect(() => {
    const checkBanStatus = async () => {
      if (!user) {
        setIsBanned(null);
        return;
      }

      try {
        const { data } = await supabase
          .from("profiles")
          .select("is_banned")
          .eq("id", user.id)
          .single();

        const banned = data?.is_banned === true;
        setIsBanned(banned);

        // Redirect to /banned if user is banned and not already there
        if (banned && location.pathname !== "/banned") {
          navigate("/banned");
        }
      } catch (error) {
        console.error("Error checking ban status:", error);
        setIsBanned(false);
      }
    };

    checkBanStatus();
  }, [user, navigate, location.pathname]);

  // Redirect to auth if not logged in
  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth");
    }
  }, [user, loading, navigate]);

  // Show loading while checking auth or ban status
  if (loading || isBanned === null) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return <>{children}</>;
};
